import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { useCartSync } from "@/hooks/useCartSync";
import WhatsAppButton from "@/components/WhatsAppButton";
import ScrollToTop from "@/components/ScrollToTop";
import CookieConsent from "@/components/CookieConsent";
import Index from "./pages/Index";

// Lazy-load all secondary routes
const ShopPage = lazy(() => import("./pages/ShopPage"));
const CategoriesPage = lazy(() => import("./pages/CategoriesPage"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const ManufacturersPage = lazy(() => import("./pages/ManufacturersPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const ShippingPolicyPage = lazy(() => import("./pages/ShippingPolicyPage"));
const PaymentMethodsPage = lazy(() => import("./pages/PaymentMethodsPage"));
const WarrantyPolicyPage = lazy(() => import("./pages/WarrantyPolicyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const BrandPage = lazy(() => import("./pages/BrandPage"));
const ProductFamilyPage = lazy(() => import("./pages/ProductFamilyPage"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const ProductConditionsPage = lazy(() => import("./pages/ProductConditionsPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const MyAccountPage = lazy(() => import("./pages/MyAccountPage"));
const SEOLandingPage = lazy(() => import("./pages/SEOLandingPage"));
const ReturnPolicyPage = lazy(() => import("./pages/ReturnPolicyPage"));
const TransformerBushingsPage = lazy(() => import("./pages/TransformerBushingsPage"));
const HighVoltageBushingsPage = lazy(() => import("./pages/HighVoltageBushingsPage"));
const MediumVoltageBushingsPage = lazy(() => import("./pages/MediumVoltageBushingsPage"));
const LowVoltageBushingsPage = lazy(() => import("./pages/LowVoltageBushingsPage"));
const PorcelainBushingsPage = lazy(() => import("./pages/PorcelainBushingsPage"));
const ResinBushingsPage = lazy(() => import("./pages/ResinBushingsPage"));
const RIPBushingsPage = lazy(() => import("./pages/RIPBushingsPage"));
const RequestQuotePage = lazy(() => import("./pages/RequestQuotePage"));
const IndustrialAutomationPartsPage = lazy(() => import("./pages/IndustrialAutomationPartsPage"));
const TransformerPartsPage = lazy(() => import("./pages/TransformerPartsPage"));
const BuchholzRelayPage = lazy(() => import("./pages/BuchholzRelayPage"));
const OilLevelIndicatorPage = lazy(() => import("./pages/OilLevelIndicatorPage"));
const TransformerInsulatorsPage = lazy(() => import("./pages/TransformerInsulatorsPage"));
const DisconnectSwitchesPage = lazy(() => import("./pages/DisconnectSwitchesPage"));
const TransformerServicesPage = lazy(() => import("./pages/TransformerServicesPage"));
const TransformerRepairServicesPage = lazy(() => import("./pages/TransformerRepairServicesPage"));
const TransformerMaintenanceServicesPage = lazy(() => import("./pages/TransformerMaintenanceServicesPage"));
const TransformerInspectionServicesPage = lazy(() => import("./pages/TransformerInspectionServicesPage"));
const TransformerInspectionDiagnosticsPage = lazy(() => import("./pages/TransformerInspectionDiagnosticsPage"));
const EmergencyTransformerRepairPage = lazy(() => import("./pages/EmergencyTransformerRepairPage"));
const TransformerBushingReplacementPage = lazy(() => import("./pages/TransformerBushingReplacementPage"));
const BuchholzRelayTestingPage = lazy(() => import("./pages/BuchholzRelayTestingPage"));
const TransformerFailureCausesPage = lazy(() => import("./pages/TransformerFailureCausesPage"));
const AbbBushingsPage = lazy(() => import("./pages/AbbBushingsPage"));
const SiemensBushingsPage = lazy(() => import("./pages/SiemensBushingsPage"));
const GeBushingsPage = lazy(() => import("./pages/GeBushingsPage"));
const HitachiBushingsPage = lazy(() => import("./pages/HitachiBushingsPage"));
const HubbellBushingsPage = lazy(() => import("./pages/HubbellBushingsPage"));
const EatonBushingsPage = lazy(() => import("./pages/EatonBushingsPage"));
const PfistererBushingsPage = lazy(() => import("./pages/PfistererBushingsPage"));
const YashBushingsPage = lazy(() => import("./pages/YashBushingsPage"));
const WegBushingsPage = lazy(() => import("./pages/WegBushingsPage"));
const TbeaBushingsPage = lazy(() => import("./pages/TbeaBushingsPage"));
const TransformerBushingManufacturersPage = lazy(() => import("./pages/TransformerBushingManufacturersPage"));
const TransformerComponentIdentificationPage = lazy(() => import("./pages/TransformerComponentIdentificationPage"));
const TransformerFailureAnalysisPage = lazy(() => import("./pages/TransformerFailureAnalysisPage"));
const TransformerTechnicalConsultationPage = lazy(() => import("./pages/TransformerTechnicalConsultationPage"));
const TransformerReplacementPartsSupportPage = lazy(() => import("./pages/TransformerReplacementPartsSupportPage"));
const HardToFindTransformerPartsPage = lazy(() => import("./pages/HardToFindTransformerPartsPage"));
const TransformerTapChangersPage = lazy(() => import("./pages/TransformerTapChangersPage"));
const HuamingTapChangersPage = lazy(() => import("./pages/HuamingTapChangersPage"));
const AbbTransformerComponentsPage = lazy(() => import("./pages/AbbTransformerComponentsPage"));
const SiemensTransformerComponentsPage = lazy(() => import("./pages/SiemensTransformerComponentsPage"));
const GeTransformerComponentsPage = lazy(() => import("./pages/GeTransformerComponentsPage"));
const HitachiTransformerComponentsPage = lazy(() => import("./pages/HitachiTransformerComponentsPage"));
const HubbellTransformerComponentsPage = lazy(() => import("./pages/HubbellTransformerComponentsPage"));
const EatonTransformerComponentsPage = lazy(() => import("./pages/EatonTransformerComponentsPage"));
const QualitrolTransformerMonitoringPage = lazy(() => import("./pages/QualitrolTransformerMonitoringPage"));
const PfistererTransformerComponentsPage = lazy(() => import("./pages/PfistererTransformerComponentsPage"));
const YashTransformerComponentsPage = lazy(() => import("./pages/YashTransformerComponentsPage"));
const TransformerServiceProjectsPage = lazy(() => import("./pages/TransformerServiceProjectsPage"));
const TransformerProjectDetailPage = lazy(() => import("./pages/TransformerProjectDetailPage"));
const TransformerProject500kvaCaliforniaPage = lazy(() => import("./pages/TransformerProject500kvaCaliforniaPage"));
const TransformerOilLeakInspectionNewMexicoPage = lazy(() => import("./pages/TransformerOilLeakInspectionNewMexicoPage"));
const TransformerHvdcLeakInspectionNewYorkPage = lazy(() => import("./pages/TransformerHvdcLeakInspectionNewYorkPage"));
const TransformerGasketReplacement2000kvaPage = lazy(() => import("./pages/TransformerGasketReplacement2000kvaPage"));
const Calisto2InstallationCommissioningNewJerseyPage = lazy(() => import("./pages/Calisto2InstallationCommissioningNewJerseyPage"));
const AbbTransformerGasketReplacement10UnitBatchPage = lazy(() => import("./pages/AbbTransformerGasketReplacement10UnitBatchPage"));
// New SILO pages — core Level-2 + supporting Level-3
const TransformerGasketsPage = lazy(() => import("./pages/TransformerGasketsPage"));
const TransformerCoolingSystemsPage = lazy(() => import("./pages/TransformerCoolingSystemsPage"));
const TransformerMonitoringSystemsPage = lazy(() => import("./pages/TransformerMonitoringSystemsPage"));
const PorcelainVsPolymerBushingsPage = lazy(() => import("./pages/PorcelainVsPolymerBushingsPage"));
const BushingFailureModesPage = lazy(() => import("./pages/BushingFailureModesPage"));
const BuchholzRelayHowItWorksPage = lazy(() => import("./pages/BuchholzRelayHowItWorksPage"));
const BuchholzRelayFaultDetectionPage = lazy(() => import("./pages/BuchholzRelayFaultDetectionPage"));
const TransformerInstallationPage = lazy(() => import("./pages/TransformerInstallationPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const CartSyncProvider = ({ children }: { children: React.ReactNode }) => {
  useCartSync();
  return <>{children}</>;
};

const PageFallback = () => <div className="min-h-screen bg-background" />;

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
        <CartSyncProvider>
          <ScrollToTop />
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/categories" element={<CategoriesPage />} />
              <Route path="/category/:slug" element={<CategoryPage />} />
              <Route path="/manufacturers" element={<ManufacturersPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/shipping-policy" element={<ShippingPolicyPage />} />
              <Route path="/payment-methods" element={<PaymentMethodsPage />} />
              <Route path="/warranty-policy" element={<WarrantyPolicyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/brand/:slug" element={<BrandPage />} />
              <Route path="/brand/:brandSlug/:familySlug" element={<ProductFamilyPage />} />
              <Route path="/product/:slug" element={<ProductPage />} />
              <Route path="/product-conditions" element={<ProductConditionsPage />} />
              <Route path="/return-policy" element={<ReturnPolicyPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/my-account" element={<MyAccountPage />} />
              <Route path="/transformer-bushings" element={<TransformerBushingsPage />} />
              <Route path="/high-voltage-transformer-bushings" element={<HighVoltageBushingsPage />} />
              <Route path="/medium-voltage-transformer-bushings" element={<MediumVoltageBushingsPage />} />
              <Route path="/low-voltage-transformer-bushings" element={<LowVoltageBushingsPage />} />
              <Route path="/porcelain-transformer-bushings" element={<PorcelainBushingsPage />} />
              <Route path="/resin-transformer-bushings" element={<ResinBushingsPage />} />
              <Route path="/RIP-transformer-bushings" element={<RIPBushingsPage />} />
              <Route path="/request-quote" element={<RequestQuotePage />} />
              <Route path="/industrial-automation-parts" element={<IndustrialAutomationPartsPage />} />
              <Route path="/transformer-parts" element={<TransformerPartsPage />} />
              <Route path="/buchholz-relay" element={<BuchholzRelayPage />} />
              <Route path="/transformer-oil-level-indicator" element={<OilLevelIndicatorPage />} />
              <Route path="/transformer-insulators" element={<TransformerInsulatorsPage />} />
              <Route path="/disconnect-switches" element={<DisconnectSwitchesPage />} />
              <Route path="/transformer-services" element={<TransformerServicesPage />} />
              <Route path="/transformer-repair-services" element={<TransformerRepairServicesPage />} />
              <Route path="/transformer-maintenance-services" element={<TransformerMaintenanceServicesPage />} />
              <Route path="/transformer-inspection-services" element={<TransformerInspectionServicesPage />} />
              <Route path="/transformer-inspection-diagnostics" element={<TransformerInspectionDiagnosticsPage />} />
              <Route path="/emergency-transformer-repair" element={<EmergencyTransformerRepairPage />} />
              <Route path="/transformer-bushing-replacement" element={<TransformerBushingReplacementPage />} />
              <Route path="/buchholz-relay-testing" element={<BuchholzRelayTestingPage />} />
              <Route path="/transformer-failure-causes" element={<TransformerFailureCausesPage />} />
              <Route path="/abb-transformer-bushings" element={<AbbBushingsPage />} />
              <Route path="/siemens-transformer-bushings" element={<SiemensBushingsPage />} />
              <Route path="/ge-transformer-bushings" element={<GeBushingsPage />} />
              <Route path="/hitachi-transformer-bushings" element={<HitachiBushingsPage />} />
              <Route path="/hubbell-transformer-bushings" element={<HubbellBushingsPage />} />
              <Route path="/eaton-transformer-bushings" element={<EatonBushingsPage />} />
              <Route path="/pfisterer-transformer-bushings" element={<PfistererBushingsPage />} />
              <Route path="/yash-transformer-bushings" element={<YashBushingsPage />} />
              <Route path="/weg-transformer-bushings" element={<WegBushingsPage />} />
              <Route path="/tbea-transformer-bushings" element={<TbeaBushingsPage />} />
              <Route path="/transformer-bushing-manufacturers" element={<TransformerBushingManufacturersPage />} />
              <Route path="/transformer-component-identification" element={<TransformerComponentIdentificationPage />} />
              <Route path="/transformer-failure-analysis" element={<TransformerFailureAnalysisPage />} />
              <Route path="/transformer-technical-consultation" element={<TransformerTechnicalConsultationPage />} />
              <Route path="/transformer-replacement-parts-support" element={<TransformerReplacementPartsSupportPage />} />
              <Route path="/hard-to-find-transformer-parts" element={<HardToFindTransformerPartsPage />} />
              <Route path="/transformer-tap-changers" element={<TransformerTapChangersPage />} />
              <Route path="/huaming-tap-changers" element={<HuamingTapChangersPage />} />
              <Route path="/abb-transformer-components" element={<AbbTransformerComponentsPage />} />
              <Route path="/siemens-transformer-components" element={<SiemensTransformerComponentsPage />} />
              <Route path="/ge-transformer-components" element={<GeTransformerComponentsPage />} />
              <Route path="/hitachi-transformer-components" element={<HitachiTransformerComponentsPage />} />
              <Route path="/hubbell-transformer-components" element={<HubbellTransformerComponentsPage />} />
              <Route path="/eaton-transformer-components" element={<EatonTransformerComponentsPage />} />
              <Route path="/qualitrol-transformer-monitoring" element={<QualitrolTransformerMonitoringPage />} />
              <Route path="/pfisterer-transformer-components" element={<PfistererTransformerComponentsPage />} />
              <Route path="/yash-transformer-components" element={<YashTransformerComponentsPage />} />
              <Route path="/transformer-service-projects" element={<TransformerServiceProjectsPage />} />
              <Route path="/transformer-project-bushing-replacement" element={<TransformerProjectDetailPage />} />
              <Route path="/transformer-project-relay-installation" element={<TransformerProjectDetailPage />} />
              <Route path="/transformer-project-diagnostics" element={<TransformerProjectDetailPage />} />
              <Route path="/transformer-project-oil-leak-repair" element={<TransformerProjectDetailPage />} />
              <Route path="/transformer-project-hard-to-find-sourcing" element={<TransformerProjectDetailPage />} />
              <Route path="/transformer-project-component-identification" element={<TransformerProjectDetailPage />} />
              <Route path="/transformer-project-500kva-installation-california" element={<TransformerProject500kvaCaliforniaPage />} />
              <Route path="/transformer-oil-leak-inspection-new-mexico" element={<TransformerOilLeakInspectionNewMexicoPage />} />
              <Route path="/transformer-hvdc-leak-inspection-new-york" element={<TransformerHvdcLeakInspectionNewYorkPage />} />
              <Route path="/transformer-gasket-replacement-2000-kva-padmount" element={<TransformerGasketReplacement2000kvaPage />} />
              <Route path="/calisto-2-installation-commissioning-new-jersey" element={<Calisto2InstallationCommissioningNewJerseyPage />} />
              <Route path="/abb-transformer-gasket-replacement-10-unit-batch" element={<AbbTransformerGasketReplacement10UnitBatchPage />} />
              {/* New SILO pages */}
              <Route path="/transformer-gaskets" element={<TransformerGasketsPage />} />
              <Route path="/transformer-cooling-systems" element={<TransformerCoolingSystemsPage />} />
              <Route path="/transformer-monitoring-systems" element={<TransformerMonitoringSystemsPage />} />
              <Route path="/porcelain-vs-polymer-bushings" element={<PorcelainVsPolymerBushingsPage />} />
              <Route path="/transformer-bushing-failure-modes" element={<BushingFailureModesPage />} />
              <Route path="/buchholz-relay-how-it-works" element={<BuchholzRelayHowItWorksPage />} />
              <Route path="/buchholz-relay-fault-detection" element={<BuchholzRelayFaultDetectionPage />} />
              <Route path="/transformer-installation" element={<TransformerInstallationPage />} />
              <Route path="/:slug" element={<SEOLandingPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <CookieConsent />
        </CartSyncProvider>
        </AuthProvider>
      </BrowserRouter>
      <WhatsAppButton />
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
